/**
 * Vermittlerprovision (Partneragentur, Scout, Tippgeber) je Kooperation. Sie ist ein Aufwand der Agentur: Die Marke zahlt
 * nicht mehr, der Creator bekommt nicht weniger, der Agenturanteil wird geteilt. Übliche Formen in DACH:
 *   Prozent des Agenturumsatzes  („10 % von dem, was wir verdienen“: Agenturanteil samt Anteil am Zusatz und Agenturgebühr)
 *   Prozent des Honorars         („5 % vom Deal an die Partneragentur“)
 *   Festbetrag                   (einmalig, im ersten Monat mit Honorar)
 * Rein und ohne Datenbank; Beträge in Cent, kaufmännisch gerundet.
 */
import { DomainError, type Cents } from "./types.js";

export type ReferralTerms =
  | { mode: "none" }
  | { mode: "percent"; percent: number; base: "agency_share" | "budget"; recipient: string | null }
  | { mode: "fixed"; cents: Cents; recipient: string | null };

/**
 * Beschriftung der Basis in Formular und Aufteilung (GELD-12): „agency_share“ rechnet vom ganzen Agenturumsatz des Monats
 * (Anteil, Anteil am Zusatz und Agenturgebühr), nicht nur vom Agenturanteil, der in der Aufteilung getrennt steht.
 */
export const REFERRAL_BASE_LABEL: Readonly<Record<"agency_share" | "budget", string>> = {
  agency_share: "vom Agenturumsatz (Anteil und Gebühr)",
  budget: "vom Honorar",
};

export interface ReferralMonthInput {
  /** Honorar des Monats (Basis „Honorar“) */
  budgetCents: Cents;
  /** Agenturanteil des Monats vor der Vermittlerprovision (Anteil + Anteil am Zusatz + Gebühr) */
  managementCents: Cents;
}

export interface ReferralMonth {
  referralCents: Cents;
  /** Agentur netto nach der Vermittlerprovision; nie unter null (sonst Hinweis) */
  managementAfterCents: Cents;
  /** Die Vermittlerprovision ist höher als der Agenturanteil */
  exceedsAgencyShare: boolean;
}

/** Aus den Spalten von deal_agency_terms (0047). */
export function referralFromRow(row: {
  referral_mode?: string | null; referral_bp?: number | null; referral_cents?: number | null; referral_base?: string | null; referral_recipient?: string | null;
} | null | undefined): ReferralTerms {
  if (!row) return { mode: "none" };
  const recipient = row.referral_recipient?.trim() || null;
  if (row.referral_mode === "percent" && row.referral_bp != null) {
    return { mode: "percent", percent: row.referral_bp / 100, base: row.referral_base === "budget" ? "budget" : "agency_share", recipient };
  }
  if (row.referral_mode === "fixed" && row.referral_cents != null) return { mode: "fixed", cents: row.referral_cents, recipient };
  return { mode: "none" };
}

/** Vermittlerprovision je Monat; der Festbetrag fällt im ersten Monat mit Honorar an. */
export function computeReferral(terms: ReferralTerms, months: readonly ReferralMonthInput[]): ReferralMonth[] {
  if (terms.mode === "percent" && !(Number.isFinite(terms.percent) && terms.percent >= 0 && terms.percent <= 100)) {
    throw new DomainError("INVALID_REFERRAL_PERCENT", String(terms.percent));
  }
  if (terms.mode === "fixed" && !(Number.isInteger(terms.cents) && terms.cents >= 0)) throw new DomainError("INVALID_REFERRAL_AMOUNT", String(terms.cents));
  let fixedUsed = false;
  return months.map((m) => {
    let cents = 0;
    if (terms.mode === "percent") {
      const base = terms.base === "budget" ? m.budgetCents : m.managementCents;
      cents = Math.round((Math.max(0, base) * terms.percent) / 100);
    } else if (terms.mode === "fixed" && !fixedUsed && m.budgetCents > 0) {
      cents = terms.cents;
      fixedUsed = true;
    }
    return { referralCents: cents, managementAfterCents: Math.max(0, m.managementCents - cents), exceedsAgencyShare: cents > m.managementCents };
  });
}
